import React, { useEffect, useRef, useState } from "react";
import { ColorFormat, PaletteType } from "../../types";
import { hexToRgb, rgbToHsl, rgbToHsv } from "../../helpers";
import Heading from "../Heading";
import ArrowSelector from "./ArrowSelector";
import ColorDisplay from "./ColorDisplay";

// Imported pickers from package (react-color)
import {
  HuePicker,
  AlphaPicker as OpacityPicker,
  GithubPicker,
} from "react-color";

export const ColorPicker: React.FC<{
  className?: string;
  includeHeading?: boolean;
  showFormatSelector?: boolean;
  showPaletteSelector?: boolean;
  showCopyColor?: boolean;
  disabled?: boolean;
  labels?: {
    colorType?: string;
    paletteType?: string;
  };
  initialColor?: string;
  initialFormat?: ColorFormat;
  barHeight?: number;
  initialPalette?: PaletteType;
  initialOpacity?: number;
  onChange?: (color: any) => void;
}> = ({
  className = "",
  includeHeading = false,
  showFormatSelector = true,
  showPaletteSelector = true,
  disabled = false,
  labels = {},
  initialColor = "#00ff00",
  initialFormat = "hex",
  initialPalette = "spectrum",
  barHeight = 100,
  initialOpacity = 1,
  showCopyColor,
  onChange,
}) => {
  const [currentColor, setCurrentColor] = useState(initialColor);
  const [opacity, setOpacity] = useState(initialOpacity);
  const [selectedFormat, setSelectedFormat] =
    useState<ColorFormat>(initialFormat);
  const [selectedPalette, setSelectedPalette] =
    useState<PaletteType>(initialPalette);

  // refs for hue container + dragging state
  const hueContainerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // Compute color values
  const rgb = hexToRgb(currentColor.replace("#", "")) || { r: 0, g: 0, b: 0 };
  const hsl = rgbToHsl(rgb);
  const hsv = rgbToHsv(rgb);

  const handleColorChange = (color: any) => {
    setCurrentColor(color.hex);
    onChange?.({
      hex: color.hex,
      rgb: color.rgb,
      hsl: color.hsl,
      alpha: opacity,
      rgba: { ...color.rgb, a: opacity },
    });
  };

  const handleOpacityChange = (color: any) => {
    const newOpacity = color.rgb.a;
    setOpacity(newOpacity);
    onChange?.({
      hex: currentColor,
      rgb: rgb,
      hsl: hsl,
      alpha: newOpacity,
      rgba: { ...rgb, a: newOpacity },
    });
  };

  // helper: set top of the internal hue indicator
  const setHueIndicatorTopFromClientY = (clientY: number) => {
    const container = hueContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    // clamp pos inside container
    let pos = clientY - rect.top;
    if (pos < 0) pos = 0;
    if (pos > rect.height) pos = rect.height;

    // primary selector used by react-color's HuePicker
    let inner = container.querySelector(
      ".hue-horizontal div"
    ) as HTMLElement | null;

    // fallback: any element with 'hue-' in class
    if (!inner) {
      inner = container.querySelector(
        '[class*="hue-"] div'
      ) as HTMLElement | null;
    }
    if (!inner) return;

    // set top in pixels
    inner.style.top = `${pos}px`;
  };

  // mouse handlers to support click + drag
  useEffect(() => {
    const onWindowMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      setHueIndicatorTopFromClientY(e.clientY);
    };
    const onWindowMouseUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
      }
    };

    window.addEventListener("mousemove", onWindowMouseMove);
    window.addEventListener("mouseup", onWindowMouseUp);

    return () => {
      window.removeEventListener("mousemove", onWindowMouseMove);
      window.removeEventListener("mouseup", onWindowMouseUp);
    };
  }, []);

  // Format options
  const formatOptions = [
    { value: "hex", label: "HEX" },
    { value: "rgb", label: "RGB" },
    { value: "hsl", label: "HSL" },
    { value: "hsv", label: "HSV" },
  ];

  // Palette options
  const paletteOptions = [
    { value: "spectrum", label: "Colorful" },
    { value: "monochrome", label: "Monochrome" },
  ];

  // Monochrome colors (različite nijanse sive)
  const monochromeColors = [
    "#000000",
    "#1a1a1a",
    "#333333",
    "#4d4d4d",
    "#666666",
    "#808080",
    "#999999",
    "#b3b3b3",
    "#cccccc",
    "#e6e6e6",
    "#f5f5f5",
    "#ffffff",
  ];

  // Renderovanje odgovarajućeg pickera na osnovu izabrane palete
  const renderColorPicker = () => {
    const colorWithOpacity = { r: rgb.r, g: rgb.g, b: rgb.b, a: opacity };

    switch (selectedPalette) {
      case "monochrome":
        return (
          <>
            <GithubPicker
              color={currentColor}
              colors={monochromeColors}
              onChange={handleColorChange}
              styles={{
                default: {
                  card: {
                    width: "100%",
                    borderRadius: "4px",
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                  },
                },
              }}
            />
            <OpacityPicker
              color={colorWithOpacity}
              className="mt-2 w-full"
              width="100%"
              onChange={handleOpacityChange}
              styles={{
                default: {
                  picker: {
                    width: "100%",
                    borderRadius: "4px",
                  },
                },
              }}
            />
          </>
        );

      case "spectrum":
      default:
        // Wrapper div catches clicks / drags and updates the internal indicator
        return (
          <>
            <div
              ref={hueContainerRef}
              style={{ height: barHeight }}
              // click: set indicator to click position
              onClick={(e) => {
                // support MouseEvent from React — use nativeClientY
                const ev = e.nativeEvent as MouseEvent;
                setHueIndicatorTopFromClientY(ev.clientY);
              }}
              // start drag
              onMouseDown={(e) => {
                draggingRef.current = true;
                const ev = e.nativeEvent as MouseEvent;
                setHueIndicatorTopFromClientY(ev.clientY);
                // note: mousemove/up handled on window in useEffect
              }}
            >
              <HuePicker
                styles={{
                  default: { picker: { height: "100%", width: "100%" } },
                }}
                color={currentColor}
                onChange={handleColorChange}
              />
            </div>

            <OpacityPicker
              color={colorWithOpacity}
              className="mt-2 w-full"
              width="100%"
              onChange={handleOpacityChange}
              styles={{
                default: {
                  picker: {
                    width: "100%",
                    borderRadius: "4px",
                  },
                },
              }}
            />
          </>
        );
    }
  };

  return (
    <div className={`pr-14 ${className}`}>
      {includeHeading && (
        <Heading
          titleWords={[
            {
              content: "Color",
              colorGradient:
                "linear-gradient(to right, rgba(56, 234, 245, 0.5), rgba(56, 234, 245, 1), rgba(56, 234, 245, 0.5))",
            },
            {
              content: "Picker",
              colorGradient:
                "linear-gradient(to right, rgba(196, 56, 245, 1), rgba(245, 56, 56, 0.5))",
            },
          ]}
          description="Lorem ipsum dolor sit amet consectetur adipiscing elit"
          className="flex flex-col justify-end items-end text-right mb-3"
          titleWordsClassName="justify-end"
        />
      )}

      <main className="p-2 relative overflow-hidden bg-gradient-to-b from-white/15 to-transparent">
        {/* Borders */}
        <>
          <div className="absolute top-0 left-0 w-full h-[2px] bg-white/50"></div>
          <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5"></div>
          <div className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-white/40 to-white/5"></div>
          <div className="absolute top-0 right-0 w-[2px] h-full bg-gradient-to-b from-white/40 to-white/5"></div>
        </>

        {/* Color Picker based on selected palette */}
        <div
          className={`mb-4 ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="rounded-md overflow-hidden">
            {renderColorPicker()}
          </div>
        </div>

        {/* Arrow Selectors */}
        <div className="space-y-1">
          {showFormatSelector && (
            <ArrowSelector
              label={labels.colorType || "Type of Color"}
              highlightedWord="Color"
              value={selectedFormat}
              options={formatOptions}
              onChange={(value) => setSelectedFormat(value as ColorFormat)}
              disabled={disabled}
            />
          )}

          {showPaletteSelector && (
            <ArrowSelector
              label={labels.paletteType || "Type of Palette"}
              highlightedWord="Palette"
              value={selectedPalette}
              options={paletteOptions}
              onChange={(value) => setSelectedPalette(value as PaletteType)}
              disabled={disabled}
            />
          )}
        </div>

        {/* Color Display with Copy */}
        {showCopyColor && (
          <ColorDisplay
            color={currentColor}
            format={selectedFormat}
            rgb={rgb}
            hsl={hsl}
            hsv={hsv}
            opacity={opacity}
          />
        )}
      </main>
    </div>
  );
};

export default ColorPicker;
